import React, { useState, useEffect } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  useTranslate,
  useNotify,
  EditButton,
  DeleteButton,
  TopToolbar,
  useRecordContext,
  useGetList,
  Loading,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
  Fab,
} from '@mui/material';
import {
  CloudQueue,
  CheckCircle,
  Cancel,
  VpnKey,
  Storage,
  Link as LinkIcon,
  LinkOff,
  Refresh,
  ArrowBack,
  Info,
  Settings,
  Security,
  AccessTime,
  Person,
  CalendarToday,
  DnsOutlined,
  Add,
  Close,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { tokenManager } from '../../utils/tokenManager';
import { API_CONFIG } from '../../config/api';

const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const translate = useTranslate();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      mb: { xs: 2, sm: 4 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => redirect('/supabase-connections')}
          size="large"
          sx={{
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
          title={translate('ra.action.back')}
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {record?.name}
            </Typography>
            {record && (
              <>
                <Chip
                  label={translate(`resources.supabase-connections.environments.${record.environment}`) || record.environment}
                  size="small"
                  sx={{
                    backgroundColor:
                      record.environment === 'production'
                        ? '#FFEBEE'
                        : record.environment === 'staging'
                        ? '#FFF3E0'
                        : record.environment === 'uat'
                        ? '#F3E5F5'
                        : '#E3F2FD',
                    color:
                      record.environment === 'production'
                        ? '#C62828'
                        : record.environment === 'staging'
                        ? '#F57C00'
                        : record.environment === 'uat'
                        ? '#6A1B9A'
                        : '#1565C0',
                    fontWeight: 500,
                  }}
                />
                {record.is_active ? (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label={translate('resources.supabase-connections.statuses.active')}
                    size="small"
                    sx={{
                      backgroundColor: '#E8F5E9',
                      color: '#00C853',
                    }}
                  />
                ) : (
                  <Chip
                    icon={<Cancel sx={{ fontSize: 16 }} />}
                    label={translate('resources.supabase-connections.statuses.inactive')}
                    size="small"
                    sx={{
                      backgroundColor: '#F5F5F5',
                      color: '#9E9E9E',
                    }}
                  />
                )}
              </>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {translate('resources.supabase-connections.show.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
        <EditButton
          label={translate('ra.action.edit')}
          variant="contained"
          sx={{
            backgroundColor: '#00C853',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#00A843',
              boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
            },
          }}
        />
        <DeleteButton
          label={translate('ra.action.delete')}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            py: 1.5,
            borderColor: '#E0E0E0',
            '&:hover': {
              borderColor: '#D32F2F',
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            },
          }}
        />
      </Box>
    </Box>
  );
};

const ConnectionDetailsSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const notify = useNotify();

  const handleTestConnection = async () => {
    if (!record) return;

    setTesting(true);
    setTestResult(null);

    try {
      const accessToken = await tokenManager.getAccessToken();
      if (!accessToken) {
        notify('Authentication required. Please log in again.', { type: 'error' });
        setTesting(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/backend/supabase-connections/${record.id}/test`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        setTestResult({ success: true, message: translate('resources.supabase-connections.messages.connectionSuccess') });
        notify('Connection test successful', { type: 'success' });
      } else {
        setTestResult({ success: false, message: result.error || translate('resources.supabase-connections.messages.connectionFailed') });
        notify(`Connection test failed: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Unknown error' });
      notify(`Connection test error: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 3, gap: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudQueue sx={{ color: '#00C853' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {translate('resources.supabase-connections.sections.connectionDetails')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {testing
              ? translate('resources.supabase-connections.actions.testing')
              : translate('resources.supabase-connections.actions.testConnection')}
          </Button>
        </Box>

        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            sx={{ mb: 3 }}
            onClose={() => setTestResult(null)}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              {translate('resources.supabase-connections.fields.supabaseUrl')}
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {record?.supabase_url}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              {translate('resources.supabase-connections.fields.environment')}
            </Typography>
            <Chip
              label={translate(`resources.supabase-connections.environments.${record?.environment}`) || record?.environment}
              size="small"
              sx={{
                backgroundColor:
                  record?.environment === 'production'
                    ? '#FFEBEE'
                    : record?.environment === 'staging'
                    ? '#FFF3E0'
                    : record?.environment === 'uat'
                    ? '#F3E5F5'
                    : '#E3F2FD',
                color:
                  record?.environment === 'production'
                    ? '#C62828'
                    : record?.environment === 'staging'
                    ? '#F57C00'
                    : record?.environment === 'uat'
                    ? '#6A1B9A'
                    : '#1565C0',
                fontWeight: 500,
              }}
            />
          </Box>

          {record?.description && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {translate('resources.supabase-connections.fields.description')}
              </Typography>
              <Typography variant="body1">
                {record.description}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const CredentialsSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <VpnKey sx={{ color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {translate('resources.supabase-connections.sections.credentials')}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {translate('resources.supabase-connections.info.existingCredentials')}
        </Alert>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {record?.encrypted_service_key && (
            <Chip
              icon={<VpnKey />}
              label={translate('resources.supabase-connections.badges.serviceKey')}
              sx={{
                backgroundColor: '#E8F5E8',
                color: '#1B5E20',
              }}
            />
          )}
          {record?.database_url_encrypted && (
            <Chip
              icon={<Storage />}
              label={translate('resources.supabase-connections.badges.databaseUrl')}
              sx={{
                backgroundColor: '#FFF3E0',
                color: '#E65100',
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ConnectedServersSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [servers, setServers] = useState<any[]>([]);
  const [availableServers, setAvailableServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedServers, setSelectedServers] = useState<{ [key: string]: boolean }>({});
  const [primaryServer, setPrimaryServer] = useState<string>('');

  useEffect(() => {
    if (record?.id) {
      loadConnectedServers();
    }
  }, [record?.id]);

  const loadConnectedServers = async () => {
    if (!record?.id) return;

    try {
      const accessToken = await tokenManager.getAccessToken();
      const response = await fetch(
        `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();
      if (result.data?.ocpp_server_connections) {
        setServers(result.data.ocpp_server_connections);
      }
    } catch (error) {
      console.error('Failed to load connected servers:', error);
    }
  };

  const loadAvailableServers = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const accessToken = await tokenManager.getAccessToken();
      const response = await fetch(
        `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}/available-servers`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();
      if (result.data) {
        setAvailableServers(result.data);
        const preSelected: { [key: string]: boolean } = {};
        result.data.forEach((server: any) => {
          if (server.isConnected) {
            preSelected[server.id] = true;
            if (server.isPrimary) {
              setPrimaryServer(server.id);
            }
          }
        });
        setSelectedServers(preSelected);
      }
    } catch (error) {
      console.error('Failed to load available servers:', error);
      notify('Failed to load available servers', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    loadAvailableServers();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedServers({});
    setPrimaryServer('');
  };

  const handleToggleServer = (serverId: string) => {
    setSelectedServers(prev => ({
      ...prev,
      [serverId]: !prev[serverId],
    }));

    if (selectedServers[serverId] && primaryServer === serverId) {
      setPrimaryServer('');
    }
  };

  const handleSaveAssignments = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const accessToken = await tokenManager.getAccessToken();

      const currentlyConnected = availableServers
        .filter(s => s.isConnected)
        .map(s => s.id);

      const toAdd = Object.keys(selectedServers)
        .filter(id => selectedServers[id] && !currentlyConnected.includes(id));
      const toRemove = currentlyConnected
        .filter(id => !selectedServers[id]);

      for (const serverId of toRemove) {
        await fetch(
          `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}/unassign/${serverId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );
      }

      for (const serverId of Object.keys(selectedServers)) {
        if (selectedServers[serverId]) {
          const isPrimary = serverId === primaryServer;
          const server = availableServers.find(s => s.id === serverId);

          if (toAdd.includes(serverId) || (server?.isConnected && server?.isPrimary !== isPrimary)) {
            await fetch(
              `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}/assign`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                  ocppServerId: serverId,
                  isPrimary,
                }),
              }
            );
          }
        }
      }

      notify('Server assignments updated successfully', { type: 'success' });
      handleCloseDialog();
      loadConnectedServers();

    } catch (error) {
      console.error('Failed to update server assignments:', error);
      notify('Failed to update server assignments', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkServer = async (serverId: string) => {
    if (!record?.id) return;

    try {
      const accessToken = await tokenManager.getAccessToken();
      const response = await fetch(
        `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}/unassign/${serverId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        notify('Server unlinked successfully', { type: 'success' });
        loadConnectedServers();
      } else {
        throw new Error('Failed to unlink server');
      }
    } catch (error) {
      console.error('Failed to unlink server:', error);
      notify('Failed to unlink server', { type: 'error' });
    }
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 3, gap: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DnsOutlined sx={{ color: '#00C853' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {translate('resources.supabase-connections.sections.connectedServers')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleOpenDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {translate('resources.supabase-connections.actions.assignServers')}
          </Button>
        </Box>

        {servers.length === 0 ? (
          <Alert severity="info">
            {translate('resources.supabase-connections.messages.noConnectedServers')}
          </Alert>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {servers.map((connection: any) => (
              <Paper key={connection.ocpp_server_id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {connection.ocpp_server?.name || connection.ocpp_server_id}
                  </Typography>
                  <Chip
                    label={connection.ocpp_server?.status || 'Unknown'}
                    size="small"
                    sx={{
                      backgroundColor: connection.ocpp_server?.status === 'online' ? '#E8F5E9' : '#F5F5F5',
                      color: connection.ocpp_server?.status === 'online' ? '#00C853' : '#9E9E9E',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    {connection.is_primary && (
                      <Chip
                        label={translate('resources.supabase-connections.badges.primary')}
                        size="small"
                        sx={{
                          backgroundColor: '#FFF8E1',
                          color: '#F9A825',
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleUnlinkServer(connection.ocpp_server_id)}
                    title={translate('resources.supabase-connections.actions.unlinkServer')}
                  >
                    <LinkOff />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{translate('resources.ocpp-servers.fields.name')}</TableCell>
                  <TableCell>{translate('resources.ocpp-servers.fields.status')}</TableCell>
                  <TableCell>{translate('resources.ocpp-servers.fields.isPrimary')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map((connection: any) => (
                  <TableRow key={connection.ocpp_server_id}>
                    <TableCell>{connection.ocpp_server?.name || connection.ocpp_server_id}</TableCell>
                    <TableCell>
                      <Chip
                        label={connection.ocpp_server?.status || 'Unknown'}
                        size="small"
                        sx={{
                          backgroundColor: connection.ocpp_server?.status === 'online' ? '#E8F5E9' : '#F5F5F5',
                          color: connection.ocpp_server?.status === 'online' ? '#00C853' : '#9E9E9E',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {connection.is_primary && (
                        <Chip
                          label={translate('resources.supabase-connections.badges.primary')}
                          size="small"
                          sx={{
                            backgroundColor: '#FFF8E1',
                            color: '#F9A825',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleUnlinkServer(connection.ocpp_server_id)}
                        title={translate('resources.supabase-connections.actions.unlinkServer')}
                      >
                        <LinkOff />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {translate('resources.supabase-connections.dialogs.assignServers.title')}
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {translate('resources.supabase-connections.dialogs.assignServers.info')}
                </Alert>

                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {availableServers.map((server) => (
                      <Paper key={server.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Checkbox
                            checked={selectedServers[server.id] || false}
                            onChange={() => handleToggleServer(server.id)}
                            sx={{ p: 0.5 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                            {server.name}
                          </Typography>
                          <Chip
                            label={server.environment || 'N/A'}
                            size="small"
                            sx={{
                              backgroundColor:
                                server.environment === 'production'
                                  ? '#FFEBEE'
                                  : server.environment === 'staging'
                                  ? '#FFF3E0'
                                  : '#E3F2FD',
                              color:
                                server.environment === 'production'
                                  ? '#C62828'
                                  : server.environment === 'staging'
                                  ? '#F57C00'
                                  : '#1565C0',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 4.5 }}>
                          <Chip
                            label={server.status || 'Unknown'}
                            size="small"
                            sx={{
                              backgroundColor: server.status === 'online' ? '#E8F5E9' : '#F5F5F5',
                              color: server.status === 'online' ? '#00C853' : '#9E9E9E',
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {translate('resources.ocpp-servers.fields.isPrimary')}
                            </Typography>
                            <Checkbox
                              size="small"
                              checked={primaryServer === server.id}
                              onChange={() => setPrimaryServer(server.id)}
                              disabled={!selectedServers[server.id]}
                              sx={{ p: 0.5 }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>{translate('resources.ocpp-servers.fields.name')}</TableCell>
                          <TableCell>{translate('resources.ocpp-servers.fields.environment')}</TableCell>
                          <TableCell>{translate('resources.ocpp-servers.fields.status')}</TableCell>
                          <TableCell>{translate('resources.ocpp-servers.fields.isPrimary')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableServers.map((server) => (
                          <TableRow key={server.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedServers[server.id] || false}
                                onChange={() => handleToggleServer(server.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {server.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={server.environment || 'N/A'}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    server.environment === 'production'
                                      ? '#FFEBEE'
                                      : server.environment === 'staging'
                                      ? '#FFF3E0'
                                      : '#E3F2FD',
                                  color:
                                    server.environment === 'production'
                                      ? '#C62828'
                                      : server.environment === 'staging'
                                      ? '#F57C00'
                                      : '#1565C0',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={server.status || 'Unknown'}
                                size="small"
                                sx={{
                                  backgroundColor: server.status === 'online' ? '#E8F5E9' : '#F5F5F5',
                                  color: server.status === 'online' ? '#00C853' : '#9E9E9E',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                checked={primaryServer === server.id}
                                onChange={() => setPrimaryServer(server.id)}
                                disabled={!selectedServers[server.id]}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {availableServers.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {translate('resources.supabase-connections.messages.noAvailableServers')}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
              {translate('ra.action.cancel')}
            </Button>
            <Button
              onClick={handleSaveAssignments}
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: '#00C853',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#00A843',
                },
              }}
            >
              {translate('ra.action.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const MetadataSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Info sx={{ color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {translate('resources.supabase-connections.sections.metadata')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                {translate('resources.supabase-connections.fields.createdAt')}
              </Typography>
            </Box>
            <Typography variant="body1">
              {record?.created_at && new Date(record.created_at).toLocaleString()}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                {translate('resources.supabase-connections.fields.updatedAt')}
              </Typography>
            </Box>
            <Typography variant="body1">
              {record?.updated_at && new Date(record.updated_at).toLocaleString()}
            </Typography>
          </Box>

          {record?.created_by && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {translate('resources.supabase-connections.fields.createdBy')}
                </Typography>
              </Box>
              <Typography variant="body1">
                {record.created_by}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const MobileDeleteButton = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) return null;

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <DeleteButton
        label={translate('ra.action.delete')}
        variant="outlined"
        icon={<DeleteIcon />}
        mutationMode="pessimistic"
        sx={{
          width: '100%',
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          py: 1.5,
          borderColor: '#E0E0E0',
          color: '#D32F2F',
          '&:hover': {
            borderColor: '#D32F2F',
            backgroundColor: 'rgba(211, 47, 47, 0.04)',
          },
        }}
      />
    </Box>
  );
};

const MobileEditFab = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile || !record) return null;

  return (
    <Fab
      color="primary"
      onClick={() => redirect(`/supabase-connections/${record.id}`)}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        backgroundColor: '#00C853',
        '&:hover': { backgroundColor: '#00A843' },
        zIndex: 1100,
      }}
    >
      <EditIcon />
    </Fab>
  );
};

export const SupabaseConnectionsShow = () => {
  return (
    <Show
      actions={false}
      title={false}
      component="div"
      sx={{
        backgroundColor: 'transparent',
        '& .RaShow-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
        <PageHeader />
        <ConnectionDetailsSection />
        <CredentialsSection />
        <ConnectedServersSection />
        <MetadataSection />
        <MobileDeleteButton />
      </Box>
      <MobileEditFab />
    </Show>
  );
};
